import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { Wallet, RefreshCw, ArrowUpDown, Search, Copy, Check, Pencil, ClipboardCopy, Download, Save, Upload, Plus, Trash2 } from 'lucide-react';
import { apiGetWallets, apiAddWallet, apiRemoveWallet } from '../utils/api';
import { AUTH_FLAG, PW_KEY } from './LoginGate';

const COLS = ['BNB', 'PAYU', 'IRENA', 'AIFX', 'HERMES', 'RIDE', 'USDT'];
const NICK_KEY = 'maradona_nicknames'; // { [address]: nickname } — per-browser

const fmtToken = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return '0';
  if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (v >= 1) return v.toLocaleString(undefined, { maximumFractionDigits: 3 });
  return v.toPrecision(3);
};
const fmtUsd = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const shortAddr = (a) => `${a.slice(0, 6)}...${a.slice(-4)}`;

const TOKEN_NAV = [
  { to: '/payu', label: '🪙 PAYU' },
  { to: '/irena', label: '🌱 IRENA' },
  { to: '/aifx', label: '🤖 AIFX' },
  { to: '/hermes', label: '🔱 HERMES' },
  { to: '/ride', label: '🛵 RIDE' },
  { to: '/maradona', label: '🧉 Maradona' }
];

const WalletsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState('totalUSD');
  const [sortDir, setSortDir] = useState('desc');
  const [minUsd, setMinUsd] = useState('5'); // hide dust: < $5 by default
  const [query, setQuery] = useState('');
  const [onlyFunded, setOnlyFunded] = useState(false);
  const [copiedBulk, setCopiedBulk] = useState(false);
  const [coinFilter, setCoinFilter] = useState(null); // null = all coins; else a COL symbol
  const [newAddr, setNewAddr] = useState('');
  const [addMsg, setAddMsg] = useState(null); // { type:'ok'|'warn'|'err', text }
  const [adding, setAdding] = useState(false);

  // Nicknames + copy state — client-side only, saved per browser in localStorage.
  const [nicknames, setNicknames] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NICK_KEY)) || {}; } catch (e) { return {}; }
  });
  const [editingAddr, setEditingAddr] = useState(null);
  const [draftNick, setDraftNick] = useState('');
  const [copiedAddr, setCopiedAddr] = useState(null);

  const saveNick = useCallback((addr, name) => {
    setNicknames((prev) => {
      const next = { ...prev };
      const v = (name || '').trim();
      if (v) next[addr] = v; else delete next[addr];
      try { localStorage.setItem(NICK_KEY, JSON.stringify(next)); } catch (e) {}
      return next;
    });
  }, []);

  const copyAddr = useCallback(async (addr) => {
    try {
      await navigator.clipboard.writeText(addr);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = addr; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (e2) {}
      document.body.removeChild(ta);
    }
    setCopiedAddr(addr);
    setTimeout(() => setCopiedAddr((c) => (c === addr ? null : c)), 1200);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await apiGetWallets());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Add a wallet to the panel (persisted server-side in Redis). Warns on duplicate.
  const addWallet = useCallback(async () => {
    const addr = newAddr.trim();
    if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) {
      setAddMsg({ type: 'err', text: 'Geçersiz adres (0x + 40 karakter olmalı)' });
      return;
    }
    setAdding(true);
    setAddMsg(null);
    try {
      let password = '';
      try { password = JSON.parse(localStorage.getItem(PW_KEY)) || ''; } catch (e) {}
      const r = await apiAddWallet({ address: addr, password });
      if (r.duplicate) {
        setAddMsg({ type: 'warn', text: 'Bu cüzdan zaten listede var' });
      } else if (r.added) {
        setAddMsg({ type: 'ok', text: 'Cüzdan eklendi' });
        setNewAddr('');
        load(); // refresh balances with the new wallet included
      }
    } catch (e) {
      setAddMsg({ type: 'err', text: e.message });
    } finally {
      setAdding(false);
    }
  }, [newAddr, load]);

  // Remove a wallet from the panel (reversible — re-add brings it back).
  const removeWallet = useCallback(async (addr) => {
    const nick = (() => { try { return JSON.parse(localStorage.getItem(NICK_KEY))[addr]; } catch (e) { return null; } })();
    if (!window.confirm(`Bu cüzdanı listeden sil?\n${nick ? nick + '\n' : ''}${addr}`)) return;
    try {
      let password = '';
      try { password = JSON.parse(localStorage.getItem(PW_KEY)) || ''; } catch (e) {}
      await apiRemoveWallet({ address: addr, password });
      load(); // refresh without the removed wallet
    } catch (e) {
      setAddMsg({ type: 'err', text: e.message });
    }
  }, [load]);

  const handleLogout = () => {
    try { localStorage.removeItem(AUTH_FLAG); localStorage.removeItem(PW_KEY); } catch (e) {}
    window.location.href = '/';
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const rows = useMemo(() => {
    if (!data || !data.wallets) return [];
    const min = parseFloat(minUsd) || 0;
    const q = query.trim().toLowerCase();
    // With a coin card selected, Min $ applies to THAT coin's USD value in the
    // wallet (so dust like 1e-8 AIFX doesn't count as holding it); on Tümü it
    // applies to the wallet's total value.
    const coinUsd = (w) => (w[coinFilter] || 0) * ((data.priceUsd && data.priceUsd[coinFilter]) || 0);
    let r = data.wallets.filter((w) => (coinFilter ? coinUsd(w) : w.totalUSD) >= min);
    if (coinFilter) r = r.filter((w) => (w[coinFilter] || 0) > 0); // only wallets holding this coin
    if (onlyFunded) r = r.filter((w) => w.totalUSD > 0);
    if (q) r = r.filter((w) => w.address.toLowerCase().includes(q) || (nicknames[w.address] || '').toLowerCase().includes(q));
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...r].sort((a, b) => {
      if (sortKey === 'nickname') {
        const an = nicknames[a.address] || '';
        const bn = nicknames[b.address] || '';
        if (!an && !bn) return 0;
        if (!an) return 1;   // unnamed always sink to the bottom
        if (!bn) return -1;
        return an.toLowerCase() < bn.toLowerCase() ? -1 * dir : an.toLowerCase() > bn.toLowerCase() ? 1 * dir : 0;
      }
      const av = sortKey === 'address' ? a.address.toLowerCase() : a[sortKey];
      const bv = sortKey === 'address' ? b.address.toLowerCase() : b[sortKey];
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [data, sortKey, sortDir, minUsd, query, nicknames, onlyFunded, coinFilter]);

  // Card click: filter to wallets holding that coin and sort by it (Total = all).
  const pickCoin = (coin) => {
    setCoinFilter(coin);
    if (coin) { setSortKey(coin); setSortDir('desc'); }
    else { setSortKey('totalUSD'); setSortDir('desc'); }
  };

  // --- Export / clipboard / nickname backup helpers ---
  const download = (filename, text, mime) => {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCsv = useCallback(() => {
    const head = ['#', 'Address', 'Nickname', ...COLS, 'TotalUSD'];
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [head.join(',')];
    rows.forEach((w, i) => {
      lines.push([i + 1, w.address, nicknames[w.address] || '', ...COLS.map((c) => w[c]), w.totalUSD.toFixed(2)].map(esc).join(','));
    });
    download(`maradona-wallets-${rows.length}.csv`, lines.join('\n'), 'text/csv');
  }, [rows, nicknames]);

  const copyFiltered = useCallback(async () => {
    const text = rows.map((w) => w.address).join('\n');
    try { await navigator.clipboard.writeText(text); }
    catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (e2) {}
      document.body.removeChild(ta);
    }
    setCopiedBulk(true);
    setTimeout(() => setCopiedBulk(false), 1500);
  }, [rows]);

  const backupNicks = useCallback(() => {
    download('maradona-nicknames.json', JSON.stringify(nicknames, null, 2), 'application/json');
  }, [nicknames]);

  const restoreNicks = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const obj = JSON.parse(reader.result);
        if (obj && typeof obj === 'object') {
          setNicknames((prev) => {
            const next = { ...prev, ...obj };
            try { localStorage.setItem(NICK_KEY, JSON.stringify(next)); } catch (e) {}
            return next;
          });
        }
      } catch (e) { /* bad file — ignore */ }
    };
    reader.readAsText(file);
  }, []);

  const totals = data && data.totals ? data.totals : {};
  const priceUsd = data && data.priceUsd ? data.priceUsd : {};
  const supply = data && data.supply ? data.supply : {};
  // % of a token's total supply held (in `amount`). Returns null when N/A.
  const supplyPct = (sym, amount) => {
    const s = supply[sym];
    if (!s || !(amount > 0)) return s ? 0 : null;
    return (amount / s) * 100;
  };
  const fmtPct = (p) => (p == null ? '—' : (p < 0.01 && p > 0 ? '<0.01' : p.toFixed(p < 1 ? 3 : 2)) + '%');

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="glass border-b border-gray-800 backdrop-blur-lg bg-opacity-95 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Wallet className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Maradona · Wallets</h1>
              <p className="text-xs text-gray-400">
                {data ? `${data.count} wallets · BNB $${(data.bnbUsd || 0).toFixed(2)}` : 'Loading...'}
              </p>
            </div>
          </div>

          <nav className="flex items-center space-x-1 bg-gray-800 p-1 rounded-xl border border-gray-700">
            {TOKEN_NAV.map((n) => (
              <NavLink key={n.to} to={n.to}
                className={({ isActive }) => `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center space-x-2">
            <button onClick={load} disabled={loading} className="btn-success px-3 py-2 text-sm flex items-center space-x-1 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button onClick={handleLogout} className="text-gray-400 hover:text-white text-xs px-3 py-2 rounded-xl border border-gray-700">Logout</button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-6">
        {error && <div className="bg-red-900/40 border border-red-700 text-red-300 rounded-xl p-4 mb-6">{error}</div>}
        {data && data.failedReads > 0 && (
          <div className="bg-yellow-900/40 border border-yellow-700 text-yellow-300 rounded-xl p-4 mb-6">
            {data.failedReads} bakiye okunamadi — "?" olan hucreler ve toplamlar eksik olabilir. Yenile ile tekrar dene.
          </div>
        )}
        {data && data.storeDegraded && (
          <div className="bg-yellow-900/40 border border-yellow-700 text-yellow-300 rounded-xl p-4 mb-6">
            Redis'e ulasilamadi: sonradan ekledigin cuzdanlar eksik, sildiklerin gorunuyor olabilir.
          </div>
        )}

        {/* Grand totals across all wallets — click a card to filter to that coin */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
          <button
            onClick={() => pickCoin(null)}
            className={`card-modern p-4 text-left transition-all ${coinFilter === null ? 'ring-2 ring-emerald-500' : 'hover:ring-1 hover:ring-gray-600'}`}
          >
            <p className="text-xs text-gray-400">Total Value {coinFilter === null ? '' : '· Tümü'}</p>
            <p className="text-xl font-bold text-emerald-400">{fmtUsd(totals.totalUSD)}</p>
          </button>
          {COLS.map((c) => (
            <button
              key={c}
              onClick={() => pickCoin(c)}
              className={`card-modern p-4 text-left transition-all ${coinFilter === c ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-600'}`}
            >
              <p className="text-xs text-gray-400">{c}</p>
              <p className="text-lg font-bold">{fmtToken(totals[c])}</p>
              <p className="text-xs text-gray-500">{fmtUsd((totals[c] || 0) * (priceUsd[c] || 0))}</p>
              {supply[c] != null && (
                <p className="text-xs text-blue-400 mt-0.5">arz {fmtPct(supplyPct(c, totals[c]))}</p>
              )}
            </button>
          ))}
        </div>

        {/* Add wallet */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <input
            value={newAddr}
            onChange={(e) => { setNewAddr(e.target.value); setAddMsg(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') addWallet(); }}
            placeholder="Yeni cüzdan adresi (0x...)"
            className="input-modern text-sm w-96 max-w-full font-mono"
          />
          <button
            onClick={addWallet}
            disabled={adding || !newAddr.trim()}
            className="text-sm px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> {adding ? 'Ekleniyor...' : 'Ekle'}
          </button>
          {addMsg && (
            <span className={`text-sm ${addMsg.type === 'ok' ? 'text-emerald-400' : addMsg.type === 'warn' ? 'text-yellow-400' : 'text-red-400'}`}>
              {addMsg.text}
            </span>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search address"
              className="input-modern text-sm pl-9 w-56" />
          </div>
          <label className="text-xs text-gray-400 flex items-center gap-2">
            Min $
            <input value={minUsd} onChange={(e) => setMinUsd(e.target.value)} type="number" min="0"
              className="input-modern text-sm w-24" />
          </label>
          <label className="text-xs text-gray-400 flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={onlyFunded} onChange={(e) => setOnlyFunded(e.target.checked)} />
            Only funded
          </label>
          <span className="text-xs text-gray-500">{rows.length} shown</span>

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <button onClick={copyFiltered} className="text-xs px-3 py-2 rounded-xl border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 flex items-center gap-1">
              {copiedBulk ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <ClipboardCopy className="w-3.5 h-3.5" />}
              {copiedBulk ? 'Copied' : `Copy ${rows.length} addrs`}
            </button>
            <button onClick={downloadCsv} className="text-xs px-3 py-2 rounded-xl border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 flex items-center gap-1">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button onClick={backupNicks} className="text-xs px-3 py-2 rounded-xl border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 flex items-center gap-1">
              <Save className="w-3.5 h-3.5" /> Backup names
            </button>
            <label className="text-xs px-3 py-2 rounded-xl border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 flex items-center gap-1 cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> Restore
              <input type="file" accept="application/json" className="hidden"
                onChange={(e) => { if (e.target.files[0]) restoreNicks(e.target.files[0]); e.target.value = ''; }} />
            </label>
          </div>
        </div>

        {/* Table */}
        <div className="card-modern overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-3 text-gray-400 font-medium">#</th>
                <th className="text-left py-3 px-3 text-gray-400 font-medium cursor-pointer" onClick={() => toggleSort('nickname')}>
                  <span className="inline-flex items-center gap-1">Wallet / Name <ArrowUpDown className="w-3 h-3" /></span>
                </th>
                {COLS.map((c) => (
                  <th key={c} className="text-right py-3 px-3 text-gray-400 font-medium cursor-pointer" onClick={() => toggleSort(c)}>
                    <span className="inline-flex items-center gap-1">{c} <ArrowUpDown className="w-3 h-3" /></span>
                  </th>
                ))}
                <th className="text-right py-3 px-3 text-gray-400 font-medium cursor-pointer" onClick={() => toggleSort('totalUSD')}>
                  <span className="inline-flex items-center gap-1">Total USD <ArrowUpDown className="w-3 h-3" /></span>
                </th>
                <th className="text-right py-3 px-3 text-blue-400 font-medium whitespace-nowrap">
                  {coinFilter && supply[coinFilter] != null ? `% ${coinFilter} arz` : coinFilter === null ? '% Portföy' : '% arz'}
                </th>
                <th className="py-3 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w, i) => (
                <tr key={w.address} className="border-b border-gray-800 hover:bg-gray-800/50">
                  <td className="py-2.5 px-3 text-gray-500">{i + 1}</td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => copyAddr(w.address)} title="Copy address"
                        className="text-gray-500 hover:text-emerald-400 shrink-0">
                        {copiedAddr === w.address ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      <a href={`https://bscscan.com/address/${w.address}`} target="_blank" rel="noreferrer"
                        className="text-blue-400 hover:underline font-mono">{shortAddr(w.address)}</a>
                      {editingAddr === w.address ? (
                        <input
                          autoFocus
                          value={draftNick}
                          onChange={(e) => setDraftNick(e.target.value)}
                          onBlur={() => { saveNick(w.address, draftNick); setEditingAddr(null); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { saveNick(w.address, draftNick); setEditingAddr(null); }
                            if (e.key === 'Escape') setEditingAddr(null);
                          }}
                          placeholder="nickname"
                          className="input-modern text-xs py-1 w-32"
                        />
                      ) : (
                        <button
                          onClick={() => { setEditingAddr(w.address); setDraftNick(nicknames[w.address] || ''); }}
                          className="flex items-center gap-1 text-gray-400 hover:text-white group"
                          title="Set nickname"
                        >
                          {nicknames[w.address]
                            ? <span className="text-emerald-300 font-medium">{nicknames[w.address]}</span>
                            : <span className="text-gray-600 italic">+ name</span>}
                          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                        </button>
                      )}
                    </div>
                  </td>
                  {COLS.map((c) => (
                    w.err && w.err.includes(c)
                      ? <td key={c} className="py-2.5 px-3 text-right text-red-400" title="Bu bakiye okunamadi">?</td>
                      : <td key={c} className={`py-2.5 px-3 text-right ${w[c] > 0 ? 'text-white' : 'text-gray-600'}`}>{fmtToken(w[c])}</td>
                  ))}
                  <td className="py-2.5 px-3 text-right font-semibold text-emerald-400">{fmtUsd(w.totalUSD)}</td>
                  <td className="py-2.5 px-3 text-right text-blue-300">
                    {coinFilter && supply[coinFilter] != null
                      ? fmtPct(supplyPct(coinFilter, w[coinFilter]))
                      : coinFilter === null
                        ? (totals.totalUSD ? fmtPct((w.totalUSD / totals.totalUSD) * 100) : '—')
                        : '—'}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <button onClick={() => removeWallet(w.address)} title="Cüzdanı sil"
                      className="text-gray-600 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={COLS.length + 5} className="py-8 text-center text-gray-500">No wallets match the filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {loading && <p className="text-center text-gray-400 py-6">Reading balances on-chain...</p>}
      </main>
    </div>
  );
};

export default WalletsPanel;
